import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Upload,
  AlertOctagon,
  BarChart3,
  Sparkles,
  MessageSquarePlus,
} from 'lucide-react';
import {
  trainedQaApi,
  TrainedAnswerResponse,
  TrainedAnswerSummary,
  TrainedQaStats,
} from '../../services/trainedQaApi';

const PAGE_SIZE = 20;

type SortField = 'title' | 'module' | 'variants' | 'updated_at';
type SortDir = 'asc' | 'desc';

function formatRelativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TrainedQaEditor() {
  // Server-side paginated data
  const [answers, setAnswers] = useState<TrainedAnswerSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<TrainedQaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Server-side pagination / filter / sort
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Editor state
  const [editing, setEditing] = useState<TrainedAnswerResponse | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [formTitle, setFormTitle] = useState('');
  const [formAnswer, setFormAnswer] = useState('');
  const [formModule, setFormModule] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formVariants, setFormVariants] = useState('');

  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  // Generate variants job tracking
  const [generatingVariants, setGeneratingVariants] = useState(false);
  const [variantProgress, setVariantProgress] = useState<{
    total: number;
    completed: number;
    failed: number;
  } | null>(null);

  // Add-variant state (expanded row — loads full answer on demand)
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedAnswer, setExpandedAnswer] = useState<TrainedAnswerResponse | null>(null);
  const [loadingExpanded, setLoadingExpanded] = useState(false);
  const [newVariantText, setNewVariantText] = useState('');

  // ── Debounce search ────────────────────────────────────

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(0);
    }, 350);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  // ── Data loading (server-side paginated) ───────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, statsRes] = await Promise.all([
        trainedQaApi.listPaginated({
          page: currentPage,
          pageSize: PAGE_SIZE,
          search: debouncedSearch || undefined,
          module: filterModule || undefined,
          activeOnly: false,
          sortField,
          sortDir,
        }),
        trainedQaApi.stats(),
      ]);
      setAnswers(listRes.answers);
      setTotalCount(listRes.total);
      setStats(statsRes);
    } catch (err: any) {
      setError(err.message || 'Failed to load trained Q&A');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filterModule, sortField, sortDir]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-dismiss success after 5s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 5000);
    return () => clearTimeout(t);
  }, [success]);

  // ── Derived values ──────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Module list from stats (avoids needing to load all answers)
  const modules = useMemo(() => {
    if (!stats?.top_modules) return [];
    return stats.top_modules.map((m) => m.module).sort();
  }, [stats]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(0);
  };

  // ── Expand row to show variants (on-demand fetch) ──────

  const handleExpandRow = async (answerId: number) => {
    if (expandedId === answerId) {
      setExpandedId(null);
      setExpandedAnswer(null);
      setNewVariantText('');
      return;
    }
    setExpandedId(answerId);
    setExpandedAnswer(null);
    setLoadingExpanded(true);
    try {
      const full = await trainedQaApi.get(answerId);
      setExpandedAnswer(full);
    } catch (err: any) {
      setError(err.message || 'Failed to load variants');
      setExpandedId(null);
    } finally {
      setLoadingExpanded(false);
    }
  };

  // ── Editor ──────────────────────────────────────────────

  const openEditor = async (summary: TrainedAnswerSummary | null) => {
    if (summary) {
      // Fetch full answer with variants for editing
      try {
        const full = await trainedQaApi.get(summary.id);
        setEditing(full);
        setIsNew(false);
        setFormTitle(full.title);
        setFormAnswer(full.answer);
        setFormModule(full.module || '');
        setFormCategory(full.category || '');
        setFormVariants(full.variants.map((v) => v.question).join('\n'));
      } catch (err: any) {
        setError(err.message || 'Failed to load answer for editing');
      }
    } else {
      setEditing({} as TrainedAnswerResponse);
      setIsNew(true);
      setFormTitle('');
      setFormAnswer('');
      setFormModule('');
      setFormCategory('');
      setFormVariants('');
    }
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !formAnswer.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const variants = formVariants
          .split('\n')
          .map((l) => l.trim())
          .filter((l) => l.length > 0);
        if (variants.length === 0) variants.push(formTitle.trim());
        await trainedQaApi.create({
          title: formTitle.trim(),
          answer: formAnswer.trim(),
          module: formModule || undefined,
          category: formCategory || undefined,
          variants,
        });
        setSuccess('Trained answer created');
      } else if (editing) {
        await trainedQaApi.update(editing.id, {
          title: formTitle.trim(),
          answer: formAnswer.trim(),
          module: formModule || undefined,
          category: formCategory || undefined,
        });

        // Diff variants and apply add/delete operations. The update endpoint
        // doesn't accept variants directly, so reconcile through the dedicated
        // variant endpoints.
        const nextVariants = formVariants
          .split('\n')
          .map((l) => l.trim())
          .filter((l) => l.length > 0);
        const prevByQuestion = new Map(
          editing.variants.map((v) => [v.question, v.id] as const)
        );
        const toAdd = nextVariants.filter((q) => !prevByQuestion.has(q));
        const toDelete = editing.variants
          .filter((v) => !nextVariants.includes(v.question))
          .map((v) => v.id);

        for (const variantId of toDelete) {
          await trainedQaApi.deleteVariant(variantId);
        }
        if (toAdd.length > 0) {
          await trainedQaApi.addVariants(editing.id, toAdd);
        }

        setSuccess('Trained answer updated');
      }
      setEditing(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing || isNew) return;
    if (!window.confirm(`Delete "${editing.title}" and all its variants?`)) return;
    setSaving(true);
    try {
      await trainedQaApi.remove(editing.id);
      setSuccess('Trained answer deleted');
      setEditing(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Variant management ──────────────────────────────────

  const handleAddVariant = async (answerId: number) => {
    if (!newVariantText.trim()) return;
    try {
      await trainedQaApi.addVariants(answerId, [newVariantText.trim()]);
      setSuccess('Variant added');
      setNewVariantText('');
      // Refresh the expanded answer
      const full = await trainedQaApi.get(answerId);
      setExpandedAnswer(full);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Add variant failed');
    }
  };

  const handleDeleteVariant = async (variantId: number, answerId: number) => {
    if (!window.confirm('Delete this variant?')) return;
    try {
      await trainedQaApi.deleteVariant(variantId);
      setSuccess('Variant deleted');
      // Refresh the expanded answer
      const full = await trainedQaApi.get(answerId);
      setExpandedAnswer(full);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Delete variant failed');
    }
  };

  // ── Bulk import ─────────────────────────────────────────

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setError(null);
      setImporting(true);
      setImportResult(null);
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        let entries = Array.isArray(parsed) ? parsed : parsed.entries;
        if (!Array.isArray(entries) || entries.length === 0) {
          throw new Error('JSON must be a non-empty array of Q&A objects');
        }

        // Auto-detect support ticket format ({ticket_key, question, answer, related_modules?, tags?})
        // vs original Q&A format ({id, question, answer}).
        let detectedFormat = 'qa';
        if ('ticket_key' in entries[0]) {
          detectedFormat = 'support_ticket';
          entries = entries.map((entry: any, idx: number) => ({
            id: entry.ticket_key || `ST${String(idx + 1).padStart(4, '0')}`,
            question: entry.question,
            answer: entry.answer,
            module: Array.isArray(entry.related_modules) ? entry.related_modules.join(', ') : undefined,
            tags: Array.isArray(entry.tags) ? entry.tags : undefined,
          }));
        }

        const result = await trainedQaApi.bulkImport(entries);
        const formatLabel = detectedFormat === 'support_ticket' ? ' (support ticket format)' : '';
        const msg = `Imported ${result.imported} of ${result.total_in_file} entries${formatLabel}` +
          (result.skipped_truncated > 0 ? `, ${result.skipped_truncated} truncated skipped` : '') +
          (result.skipped_duplicate > 0 ? `, ${result.skipped_duplicate} duplicates skipped` : '') +
          (result.skipped_max_steps > 0 ? `, ${result.skipped_max_steps} over max-steps skipped` : '') +
          (result.failed > 0 ? `, ${result.failed} failed` : '');
        setImportResult(msg);
        setSuccess(msg);
        await loadData();
      } catch (err: any) {
        setError(err.message || 'Import failed');
      } finally {
        setImporting(false);
      }
    };
    input.click();
  };

  const handleWipeAll = async () => {
    if (!window.confirm('This will DELETE ALL trained Q&A entries and variants. This cannot be undone.\n\nAre you sure?')) return;
    if (!window.confirm('FINAL CONFIRMATION: Wipe all trained Q&A data?')) return;
    setError(null);
    try {
      const result = await trainedQaApi.wipeAll();
      setSuccess(`Wiped ${result.rows_deleted} trained answers`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Wipe failed');
    }
  };

  const handleGenerateVariants = async () => {
    if (!window.confirm('This will use Claude to generate 3 alternative phrasings for every answer that has ≤2 variants.\n\nProceed?')) return;
    setError(null);
    setGeneratingVariants(true);
    setVariantProgress(null);
    try {
      const result = await trainedQaApi.generateVariants(3, 2);
      if (!result.job_id) {
        // Nothing to do or immediate response (no background job).
        setSuccess(result.message);
        setGeneratingVariants(false);
        return;
      }

      setVariantProgress({ total: result.total_answers, completed: 0, failed: 0 });

      const job = await trainedQaApi.pollJobStatus(result.job_id, 2000, 600, (progress) => {
        setVariantProgress({
          total: progress.total,
          completed: progress.completed,
          failed: progress.failed,
        });
      });

      if (job.status === 'completed') {
        const failMsg = job.failed > 0 ? ` (${job.failed} failed)` : '';
        setSuccess(`Generated variants for ${job.completed} of ${job.total} answers${failMsg}`);
      } else {
        setError(
          `Variant generation finished with failures: ${job.failed} of ${job.total}. ` +
          (job.errors?.length ? job.errors.slice(0, 5).join('; ') : '')
        );
      }
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Generate variants failed');
    } finally {
      setGeneratingVariants(false);
      setVariantProgress(null);
    }
  };

  // ── Sort icon helper ────────────────────────────────────

  const SortIcon = ({ field }: { field: SortField }) => (
    <ArrowUpDown size={11} className={sortField === field ? 'text-blue-600' : 'text-gray-400'} />
  );

  // ── Render ──────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total Answers', value: stats.total_answers },
            { label: 'Total Variants', value: stats.total_variants },
            { label: 'With Embedding', value: stats.variants_with_embedding },
            { label: 'Avg Variants/Answer', value: stats.avg_variants_per_answer },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-lg font-semibold text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Q&A Management</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {totalCount} trained answers — matched before the RAG pipeline for instant responses
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search Q&A…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
            />
          </div>
          <select
            value={filterModule}
            onChange={(e) => { setFilterModule(e.target.value); setCurrentPage(0); }}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All modules</option>
            {modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button
            onClick={handleImport}
            disabled={importing}
            title="Import JSON — auto-detects Q&A and support ticket formats"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Import JSON
          </button>
          <button
            onClick={handleGenerateVariants}
            disabled={generatingVariants}
            title="Auto-generate variant phrasings via Claude"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-purple-300 text-purple-700 rounded-md text-xs hover:bg-purple-50 transition-colors disabled:opacity-50"
          >
            {generatingVariants ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {generatingVariants ? 'Generating…' : 'Generate Variants'}
          </button>
          <button
            onClick={handleWipeAll}
            title="Delete ALL trained Q&A data"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-red-300 text-red-600 rounded-md text-xs hover:bg-red-50 transition-colors"
          >
            <AlertOctagon size={13} /> Wipe All
          </button>
          <button
            onClick={() => openEditor(null)}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={13} /> Add Q&A
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-green-700">{success}</p>
        </div>
      )}

      {/* Generate Variants progress */}
      {generatingVariants && variantProgress && (
        <div className="border border-purple-200 rounded-lg p-3 bg-purple-50">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 size={14} className="text-purple-600 animate-spin flex-shrink-0" />
            <span className="text-xs font-semibold text-purple-800">
              Generating Variants
            </span>
            <span className="text-xs text-purple-600 ml-auto">
              {variantProgress.completed + variantProgress.failed} / {variantProgress.total}
              {variantProgress.failed > 0 && ` (${variantProgress.failed} failed)`}
            </span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2">
            <div
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${variantProgress.total > 0
                  ? Math.round(((variantProgress.completed + variantProgress.failed) / variantProgress.total) * 100)
                  : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">{isNew ? 'New Trained Q&A' : 'Edit Trained Q&A'}</h3>
            <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-100 rounded">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Title / Question</label>
              <input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="How to access the Coastal Schedule module?"
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Answer</label>
              <textarea
                value={formAnswer}
                onChange={(e) => setFormAnswer(e.target.value)}
                rows={5}
                placeholder="Step 1: Navigate to Schedules menu..."
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Module</label>
              <input
                value={formModule}
                onChange={(e) => setFormModule(e.target.value)}
                placeholder="Coastal Schedule"
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category / Source</label>
              <input
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                placeholder="HF_123_Module.docx"
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Question Variants (one per line — used for semantic matching)
              </label>
              <textarea
                value={formVariants}
                onChange={(e) => setFormVariants(e.target.value)}
                rows={3}
                placeholder={"How to access Coastal Schedule?\nWhere is the Coastal Schedule module?\nOpen Coastal Schedule"}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">
                {isNew
                  ? 'If empty, the title is used as the single variant.'
                  : 'Add a line to add a variant; remove a line to delete a variant.'}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <div>
              {!isNew && (
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-600 rounded-md text-xs hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={13} /> Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditing(null)}
                className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formTitle.trim() || !formAnswer.trim()}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {isNew ? 'Create' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 size={20} className="animate-spin mx-auto text-gray-400" />
            <p className="text-xs text-gray-500 mt-2">Loading trained Q&A…</p>
          </div>
        ) : answers.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-xs text-gray-500">
              {totalCount === 0 && !debouncedSearch && !filterModule
                ? 'No trained Q&A entries yet. Import from JSON or create one manually.'
                : 'No results match your search.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-fixed">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer w-1/2" onClick={() => toggleSort('title')}>
                    <span className="flex items-center gap-1">Question <SortIcon field="title" /></span>
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer w-28" onClick={() => toggleSort('module')}>
                    <span className="flex items-center gap-1">Module <SortIcon field="module" /></span>
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 w-28">Category</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-700 w-16">Active</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-700 cursor-pointer w-20" onClick={() => toggleSort('variants')}>
                    <span className="flex items-center gap-1 justify-center">Variants <SortIcon field="variants" /></span>
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer w-28" onClick={() => toggleSort('updated_at')}>
                    <span className="flex items-center gap-1">Updated <SortIcon field="updated_at" /></span>
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {answers.map((answer) => (
                  <tr key={answer.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-900 break-words" title={answer.title}>
                        {answer.title}
                      </p>
                      <p className="text-gray-400 break-words mt-0.5" title={answer.answer}>
                        {answer.answer.length > 300 ? `${answer.answer.substring(0, 300)}…` : answer.answer}
                      </p>
                      {/* Expandable variants (loaded on demand) */}
                      {expandedId === answer.id && (
                        <div className="mt-2 space-y-1">
                          {loadingExpanded ? (
                            <div className="flex items-center gap-1 text-gray-400">
                              <Loader2 size={12} className="animate-spin" /> Loading variants…
                            </div>
                          ) : expandedAnswer ? (
                            <>
                              <div className="text-xs text-gray-500 font-medium">Variants ({expandedAnswer.variants.length}):</div>
                              {expandedAnswer.variants.map((v) => (
                                <div key={v.id} className="flex items-center gap-1 text-gray-600">
                                  <span className="flex-1 truncate">• {v.question}</span>
                                  {expandedAnswer.variants.length > 1 && (
                                    <button
                                      onClick={() => handleDeleteVariant(v.id, answer.id)}
                                      className="p-0.5 text-red-400 hover:text-red-600"
                                      title="Delete variant"
                                    >
                                      <X size={11} />
                                    </button>
                                  )}
                                </div>
                              ))}
                              <div className="flex items-center gap-1 mt-1">
                                <input
                                  value={newVariantText}
                                  onChange={(e) => setNewVariantText(e.target.value)}
                                  placeholder="Add new question phrasing…"
                                  className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  onKeyDown={(e) => e.key === 'Enter' && handleAddVariant(answer.id)}
                                />
                                <button
                                  onClick={() => handleAddVariant(answer.id)}
                                  disabled={!newVariantText.trim()}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs disabled:opacity-50"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => { setExpandedId(null); setExpandedAnswer(null); setNewVariantText(''); }}
                                  className="px-2 py-1 text-gray-500 hover:bg-gray-100 rounded text-xs"
                                >
                                  Close
                                </button>
                              </div>
                            </>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 truncate">{answer.module || '—'}</td>
                    <td className="px-3 py-2 text-gray-600 truncate">{answer.category || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${answer.is_active ? 'bg-green-500' : 'bg-gray-300'}`} title={answer.is_active ? 'Active' : 'Inactive'} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        answer.variant_count >= 3
                          ? 'bg-green-100 text-green-800'
                          : answer.variant_count >= 2
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {answer.variant_count}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500" title={answer.updated_at}>
                      {formatRelativeDate(answer.updated_at)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => handleExpandRow(answer.id)}
                          className={`p-1 rounded ${
                            expandedId === answer.id
                              ? 'text-purple-600 bg-purple-50'
                              : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                          }`}
                          title="Manage variants"
                        >
                          <MessageSquarePlus size={14} />
                        </button>
                        <button
                          onClick={() => openEditor(answer)}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Save size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete "${answer.title}"?`)) return;
                            try {
                              await trainedQaApi.remove(answer.id);
                              setSuccess('Deleted');
                              await loadData();
                            } catch (err: any) { setError(err.message); }
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-gray-600 px-2">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
